/**
 * What «قبول» on a درخواست تسویه و خروج actually has to do, decided from the
 * member's remaining balance and the kind of صندوق they are leaving.
 *
 * The three outcomes are genuinely different actions, not three renderings of
 * one action, which is why this is a plan the screen reads rather than a
 * boolean it branches on:
 *
 *  - `ready`  — nothing is owed in either direction. Settle and remove, now.
 *  - `debt`   — the member owes the fund. Nobody can accept this until the
 *               debt is paid; Android says the same and stops there.
 *  - `payout` — the fund owes the member. Someone has to hand that money over
 *               before the membership can close, and «someone» is either
 *               Hamyan (پرداخت) or the manager themselves (فقط ثبت شود).
 *
 * Mirrors MpExitRequestsActivity.sendCheckoutStatusRequest in the Android app,
 * with one deliberate difference: when «موجودی نزد همیان» is short, Android
 * refuses the whole thing, while here «فقط ثبت شود» stays open. A manager who
 * pays a leaving member out of their own pocket is doing the ordinary thing
 * in a صندوق قرض‌الحسنه, and blocking it was how the PWA became a dead end in
 * the first place.
 */

/** trust_state values that mean the fund holds its own money — utils/constants/choice.py */
const WORRIED_TRUST_STATES = ['wduv', 'wdip', 'wdvf']

/** box types where money actually flows through Hamyan */
const PAYABLE_TYPES = ['mt_pybl', 'mt_hbrd', 'mt_hbrd_sp']

export type SettlementPlan =
    | { kind: 'ready' }
    | { kind: 'debt'; amount: number }
    | {
          kind: 'payout'
          amount: number
          /** whether «پرداخت از موجودی نزد همیان» is a real option right now */
          canPayFromHamyan: boolean
          /** why it is not, when it is not — shown next to the disabled button */
          blockedReason?: string
      }

export function settlementPlan(
    status: IGetSettlementCheckStatusResponse,
    moneypool: IMoneyPoolMoneyPool | undefined
): SettlementPlan {
    if (status.to_cashin_amount > 0) {
        return { kind: 'debt', amount: status.to_cashin_amount }
    }
    if (status.to_cashout_amount <= 0) return { kind: 'ready' }

    const amount = status.to_cashout_amount
    const isWorried = WORRIED_TRUST_STATES.includes(
        moneypool?.trust_state || ''
    )
    const goesThroughHamyan = PAYABLE_TYPES.includes(moneypool?.type || '')
    const balance = moneypool?.hamyan_balance ?? 0

    if (isWorried) {
        return {
            kind: 'payout',
            amount,
            canPayFromHamyan: false,
            blockedReason:
                'پول این صندوق نزد خودتان است، نه نزد همیان؛ پرداخت را خودتان انجام می‌دهید.',
        }
    }
    if (!goesThroughHamyan) {
        return {
            kind: 'payout',
            amount,
            canPayFromHamyan: false,
            blockedReason:
                'این صندوق ثبتی است و همیان در آن پولی نگه نمی‌دارد؛ پرداخت را خودتان انجام می‌دهید.',
        }
    }
    if (balance < amount) {
        return {
            kind: 'payout',
            amount,
            canPayFromHamyan: false,
            blockedReason:
                'موجودی نزد همیان کمتر از این مبلغ است. یا موجودی را افزایش دهید، یا خودتان پرداخت کنید و «فقط ثبت شود» را بزنید.',
        }
    }
    return { kind: 'payout', amount, canPayFromHamyan: true }
}
